import styled from 'styled-components';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import DatePicker, { DateRangeInput } from 'components/inputs/DatePicker';
import LabelWrap from 'components/inputs/LabelWrap';
import ContextMenu from 'components/ContextMenu';
import { useEffect, useMemo } from 'react';
import Button from 'components/inputs/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { GroupingLevel } from 'api/model';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import useZipCodeAreas from 'hooks/useZipCodeAreas';
import useHasAccessToRiddermarkReporting from 'hooks/useHasAccessToRiddermarkReporting';
import ManagementReportDownloadButton from '../widgets/management/ManagementReportDownloadButton';

const RIDDERMARK_SUPPLIER_ID = 1;

const ActionsWrapper = styled.div`
  padding-top: 10px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  padding: 10px;
  gap: 10px;
`;

const DateSelectWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  gap: 5px;
  margin-left: 5px;
  white-space: nowrap;
  button {
    padding: 0 10px;
    height: 30px;
  }
`;

interface DateRangeOption {
  label: string;
  range: [Date, Date];
}

const groupingOptions: Option<GroupingLevel>[] = [
  { label: 'Dag', value: GroupingLevel.Day },
  { label: 'Vecka', value: GroupingLevel.Week },
  { label: 'Månad', value: GroupingLevel.Month },
  { label: 'År', value: GroupingLevel.Year },
];

const RiddermarkKPIsFilterBar = () => {
  const {
    dashboardState: {
      daterange,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      groupingLevel,
    },
    setSelectedDateRange,
    setSelectedSupplierIDs,
    setSelectedFromCountyIDs,
    setSelectedToCountyIDs,
    setGroupingLevel,
    setExcludeCompanyNameContains,
  } = useDashboardControls();
  const { selectableZipCodeAreas } = useZipCodeAreas();
  const hasManagementReporting = useHasAccessToRiddermarkReporting();

  // Lock supplier selection to Riddermark and exclude Zäkra companies (hidden from UI)
  useEffect(() => {
    setSelectedSupplierIDs([RIDDERMARK_SUPPLIER_ID]);
    setExcludeCompanyNameContains('Zäkra'); // Exclude Zäkra companies because they are not part of Riddermark
  }, [setSelectedSupplierIDs, setExcludeCompanyNameContains]);

  const countyOptions = useMemo(() => {
    return selectableZipCodeAreas.map((zca) => ({
      label: zca.areaName,
      value: zca.id,
    }));
  }, [selectableZipCodeAreas]);

  const dateRangeOptions: DateRangeOption[] = useMemo(
    () => [
      {
        // Ledningsrapportens period: fredag 00:00 till och med torsdag 23:59, sju dygn.
        // Senast avslutade sådan vecka räknat från idag — dras rapporten på fredagen är det
        // veckan som just tog slut. Veckorna ligger kant i kant utan överlapp.
        label: 'Rapportvecka (fre–tors)',
        range: (() => {
          const now = new Date();
          // Hur många dagar sedan senaste fredag (0 = idag är fredag)
          const daysSinceFriday = (now.getDay() - 5 + 7) % 7;
          const end = new Date(now);
          end.setDate(now.getDate() - daysSinceFriday);
          end.setHours(0, 0, 0, 0);
          const start = new Date(end);
          start.setDate(end.getDate() - 7);
          // Slutet är torsdag 23:59:59 — dygnet innan fredagen perioden räknas fram till
          const inclusiveEnd = new Date(end.getTime() - 1000);
          return [start, inclusiveEnd] as [Date, Date];
        })(),
      },
      {
        label: 'Idag',
        range: [
          new Date(new Date().setHours(0, 0, 0, 0)),
          new Date(new Date().setHours(23, 59, 59, 999)),
        ],
      },
      {
        label: 'Senaste 7 dagar',
        range: [
          new Date(new Date().getTime() - 6 * 24 * 60 * 60 * 1000),
          new Date(),
        ],
      },
      {
        label: 'Innevarande månad',
        range: [
          new Date(new Date().getFullYear(), new Date().getMonth(), 1),
          new Date(
            new Date().getFullYear(),
            new Date().getMonth() + 1,
            0,
            23,
            59,
            59,
            999
          ),
        ],
      },
      {
        label: 'Förra månaden',
        range: [
          new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
          new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            0,
            23,
            59,
            59,
            999
          ),
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <ActionsWrapper>
      <Actions>
        <DateSelectWrapper>
          <LabelWrap label="Datum">
            <DatePicker
              calendarStartDay={1}
              customInput={
                <DateRangeInput daterange={[daterange[0], daterange[1]]} />
              }
              selectsRange
              startDate={daterange[0]}
              endDate={daterange[1]}
              dateFormat="yyyy-MM-dd"
              locale="sv"
              onChange={setSelectedDateRange}
              populateHolidays
              shouldCloseOnSelect
              showYearDropdown
              showMonthDropdown
              showWeekNumbers
            />
          </LabelWrap>
          <ContextMenu
            options={dateRangeOptions.map((ro) => ({
              label: ro.label,
              range: ro.range,
              onClick: () => setSelectedDateRange(ro.range),
            }))}
          >
            <Button>
              {dateRangeOptions.find(
                (ro) =>
                  daterange[0] &&
                  daterange[1] &&
                  daterange[0].getTime() === ro.range[0].getTime() &&
                  daterange[1].getTime() === ro.range[1].getTime()
              )?.label ?? 'Frival'}{' '}
              <FontAwesomeIcon icon={faAngleDown} />
            </Button>
          </ContextMenu>
        </DateSelectWrapper>
        <LabelWrap label="Gruppering">
          <ContextMenu
            options={groupingOptions.map((opt) => ({
              label: opt.label,
              onClick: () => setGroupingLevel(opt.value),
            }))}
          >
            <Button>
              {groupingOptions.find((opt) => opt.value === groupingLevel)
                ?.label ?? 'Dag'}{' '}
              <FontAwesomeIcon icon={faAngleDown} />
            </Button>
          </ContextMenu>
        </LabelWrap>
        <LabelWrap label="Från-län">
          <MultiSelect
            placeholder="Välj län"
            onChange={(newVal) => {
              setSelectedFromCountyIDs(Array.from(newVal));
            }}
            options={countyOptions}
            value={new Set(selectedFromCountyIDs)}
          />
        </LabelWrap>
        <LabelWrap label="Till-län">
          <MultiSelect
            placeholder="Välj län"
            onChange={(newVal) => {
              setSelectedToCountyIDs(Array.from(newVal));
            }}
            options={countyOptions}
            value={new Set(selectedToCountyIDs)}
          />
        </LabelWrap>
        {/*
          Uttaget är hela ledningsrapporten som Excel och har samma serverspärr som rapportens
          endpoints. Utan flaggan skulle knappen bara ge 403, så den hör ihop med widgetarna och
          inte med vyn.
        */}
        {hasManagementReporting ? (
          <LabelWrap label="Export">
            <ManagementReportDownloadButton />
          </LabelWrap>
        ) : null}
      </Actions>
    </ActionsWrapper>
  );
};

export default RiddermarkKPIsFilterBar;
